import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faHistory,
  faArrowTrendUp
} from "@fortawesome/free-solid-svg-icons";

interface CustomSearchProps {
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  placeholder?: string;
  height?: string; // e.g. '40px', '3rem', 'full', 'medium', 'small'
  width?: string;  // e.g. 'full', 'medium', 'small', '300px', etc.
  className?: string;
  showClear?: boolean;
  variant?: 'primary' | 'minimal' | 'outlined';
  showTrending?: boolean;
  searchHistoryKey?: string; // localStorage key for search history
  trendingSearches?: string[]; // pre-defined trending searches
  maxHistoryItems?: number;
  onHistoryItemClick?: (term: string) => void;
  disabled?: boolean;
}

const getWidthClass = (width: string): string => {
  if (width === "medium") return "w-64";
  if (width === "small") return "w-40";
  if (!["|full", "medium", "small"].includes(width)) return "";
  return "w-full";
};

const getHeightStyle = (height: string): React.CSSProperties => {
  if (height === "full") return { height: "100%" };
  if (height === "medium") return { height: "30px" };
  if (height === "small") return { height: "26px" };
  return { height };
};

const getCustomWidthStyle = (width: string): React.CSSProperties => {
  if (["full", "medium", "small"].includes(width)) return {};
  return { width };
};

const CustomSearch: React.FC<CustomSearchProps> = ({
  value = "",
  onChange,
  onSearch,
  placeholder = "Search...",
  height = "30px",
  width = "full",
  className = "",
  showClear = true,
  variant = "primary",
  showTrending = false,
  searchHistoryKey = "search_history",
  trendingSearches = [],
  maxHistoryItems = 5,
  onHistoryItemClick,
  disabled = false,
}) => {
  const [searchValue, setSearchValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  console.log(isFocused);
  // Load search history from localStorage
  useEffect(() => {
    const history = localStorage.getItem(searchHistoryKey);
    if (history) {
      setSearchHistory(JSON.parse(history));
    }
  }, [searchHistoryKey]);

  // Handle outside click to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (onChange) onChange(e.target.value);

    // Show suggestions if there's a value or history/trending items
    if (e.target.value || searchHistory.length > 0 || trendingSearches.length > 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSearch = () => {
    if (!searchValue.trim()) return;

    // Add to search history if not already present
    if (searchValue && !searchHistory.includes(searchValue)) {
      const newHistory = [searchValue, ...searchHistory].slice(0, maxHistoryItems);
      setSearchHistory(newHistory);
      localStorage.setItem(searchHistoryKey, JSON.stringify(newHistory));
    }

    if (onSearch) onSearch(searchValue);
    setShowSuggestions(false);
  };

  const handleClear = () => {
    setSearchValue("");
    if (onChange) onChange("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (term: string) => {
    setSearchValue(term);
    if (onChange) onChange(term);
    if (onHistoryItemClick) onHistoryItemClick(term);
    if (onSearch) onSearch(term);
    setShowSuggestions(false);
  };

  // Dynamic width/height/custom-width via extracted helpers
  const widthClass = getWidthClass(width);
  const heightStyle = getHeightStyle(height ?? "");
  const customWidthStyle = getCustomWidthStyle(width);

  // Variant styles
  let inputClass = "";
  switch (variant) {
    case "minimal":
      inputClass =
        "border-0 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:bg-white dark:focus:bg-gray-600";
      break;

    case "outlined":
      inputClass =
        "border border-gray-300 dark:border-gray-600 bg-transparent text-gray-800 dark:text-gray-200 focus:border-green-500 dark:focus:border-green-400";
      break;

    default: // primary, fixed dark mode issue of search field 
      inputClass =
        "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:border-transparent";
  }

  // Filter suggestions based on input
  const filteredHistory = searchValue
    ? searchHistory.filter(item =>
      item.toLowerCase().includes(searchValue.toLowerCase()))
    : searchHistory;

  const filteredTrending = searchValue
    ? trendingSearches.filter(item =>
      item.toLowerCase().includes(searchValue.toLowerCase()))
    : trendingSearches;

  return (
    <div
      ref={containerRef}
      className={`relative ${widthClass} ${className}`}
      style={{ ...heightStyle, ...customWidthStyle }}
    >
      <div className="relative flex items-center w-full h-full">
        <div className="absolute left-2 text-gray-400 flex items-center h-full">
          {/* <FontAwesomeIcon icon={faSearch} size="2xs" /> */}
        </div>

        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsFocused(true);
            if (searchHistory.length > 0 || trendingSearches.length > 0) {
              setShowSuggestions(true);
            }
          }}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          className={`rounded-lg px-8 py-2 w-full outline-none transition-all text-xs ${inputClass} ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""
            }`}
          style={{ lineHeight: '1', minHeight: '22px' }}
        />

        {showClear && searchValue && (
          <button
            type="button"
            className="absolute right-2 text-gray-400 hover:text-gray-600 focus:outline-none flex items-center h-full"
            onClick={handleClear}
            aria-label="Clear search"
          >
            <FontAwesomeIcon icon={faTimes} size="2xs" />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && (showTrending || searchHistory.length > 0) && (
        <div className="absolute z-50 mt-0.5 w-full bg-white rounded-md shadow-lg py-0.5 max-h-48 overflow-auto text-xs">
          {/* Search history section */}
          {filteredHistory.length > 0 && (
            <div>
              <div className="px-2 py-0.5 text-xs font-semibold text-gray-500 flex items-center">
                <FontAwesomeIcon icon={faHistory} className="mr-1" size="xs" />
                Recent Searches
              </div>
              {filteredHistory.map((term) => (
                <button
                  key={`history-${term}`}
                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 flex items-center"
                  onClick={() => handleSuggestionClick(term)}
                >
                  <FontAwesomeIcon icon={faHistory} className="mr-1.5 text-gray-400" size="xs" />
                  {term}
                </button>
              ))}
            </div>
          )}

          {/* Trending searches section */}
          {showTrending && filteredTrending.length > 0 && (
            <div>
              <div className="px-2 py-0.5 text-xs font-semibold text-gray-500 flex items-center">
                <FontAwesomeIcon icon={faArrowTrendUp} className="mr-1" size="xs" />
                Trending Searches
              </div>
              {filteredTrending.map((term) => (
                <button
                  key={`trending-${term}`}
                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 flex items-center"
                  onClick={() => handleSuggestionClick(term)}
                >
                  <FontAwesomeIcon icon={faArrowTrendUp} className="mr-1.5 text-blue-400" size="xs" />
                  {term}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSearch;